import {useCallback, useMemo} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import {useSuiContext} from '@s-ui/react-context'
import IconChevronLeft from '@adv-mt/mt-svg-icons/lib/ChevronLeft'
import IconChevronRight from '@adv-mt/mt-svg-icons/lib/ChevronRight'

import {BASE_CLASS, DESKTOP_GRID_ROWS, DESKTOP_MIN_CELLS, MEDIA_TYPES, mediaItemShape} from './config.js'
import GalleryControls from './GalleryControls.js'
import GalleryIconButton from './GalleryIconButton.js'
import GalleryMediaItem from './GalleryMediaItem.js'
import {GALLERY_TAG_TYPES} from './GalleryTag.js'
import {getMediaItemKey} from './helpers.js'
import useDesktopGalleryScroll from './useDesktopGalleryScroll.js'

const FEATURED_INDEX = 0
const FIRST_THUMBNAIL_INDEX = 1

const buildDesktopItems = mediaItems => {
  const thumbnailCount = mediaItems.length - 1
  const placeholderCount = Math.max(
    DESKTOP_MIN_CELLS - mediaItems.length,
    thumbnailCount % DESKTOP_GRID_ROWS !== 0 ? 1 : 0
  )
  return placeholderCount > 0
    ? [...mediaItems, ...Array(placeholderCount).fill({type: MEDIA_TYPES.PLACEHOLDER})]
    : mediaItems
}

const DesktopGallery = ({
  mediaItems,
  alt,
  galleryLabel,
  hasVideo,
  showControls = true,
  videoIndex,
  canPlayInline,
  isModalOpen = false,
  onOpenGallery,
  onVideoButtonClick,
  onVideoPlayingChange
}) => {
  const {i18n} = useSuiContext()

  const desktopItems = useMemo(() => buildDesktopItems(mediaItems), [mediaItems])

  const {
    scrollRef,
    firstThumbRef,
    featuredRef,
    atStart,
    atEnd,
    visibleCells,
    isFeaturedVisible,
    scrollPrev,
    scrollNext
  } = useDesktopGalleryScroll(desktopItems.length, mediaItems.length)

  const getCellRef = index => {
    if (index === FEATURED_INDEX) return featuredRef
    if (index === FIRST_THUMBNAIL_INDEX) return firstThumbRef
    return null
  }

  const handleOpenGalleryFirst = useCallback(() => onOpenGallery?.(FEATURED_INDEX, {isButton: true}), [onOpenGallery])
  const handleOpenGalleryVideo = useCallback(() => {
    const el = scrollRef.current
    const cell = el?.children[videoIndex]
    if (el && cell) {
      const trackRect = el.getBoundingClientRect()
      const cellRect = cell.getBoundingClientRect()
      const isCellVisible = cellRect.right > trackRect.left && cellRect.left < trackRect.right
      if (isCellVisible) {
        onOpenGallery?.(videoIndex, {isButton: true})
        return
      }
      cell.scrollIntoView({behavior: 'smooth', block: 'nearest', inline: 'nearest'})
      return
    }
    onOpenGallery?.(videoIndex, {isButton: true})
  }, [videoIndex, onOpenGallery])

  return (
    <>
      <div ref={scrollRef} className={`${BASE_CLASS}-track`}>
        {desktopItems.map((item, i) => (
          <div
            key={getMediaItemKey(item, i)}
            ref={getCellRef(i)}
            className={cx(`${BASE_CLASS}-cell`, i === FEATURED_INDEX && `${BASE_CLASS}-cell--featured`)}
          >
            {visibleCells.has(i) && (
              <GalleryMediaItem
                item={item}
                alt={alt}
                index={i}
                isPriority={i === FEATURED_INDEX}
                isActive={(i !== FEATURED_INDEX || isFeaturedVisible) && !isModalOpen}
                canPlayInline={canPlayInline}
                onOpenGallery={onOpenGallery}
                onVideoButtonClick={onVideoButtonClick}
                onVideoPlayingChange={onVideoPlayingChange}
              />
            )}
          </div>
        ))}
      </div>

      {showControls && (
        <GalleryControls
          className={`${BASE_CLASS}-controls`}
          counterType={GALLERY_TAG_TYPES.IMAGE}
          counterLabel={galleryLabel}
          onCounterClick={handleOpenGalleryFirst}
          hasVideo={hasVideo}
          videoLabel={i18n.t('COMMON.VIDEO')}
          onVideoClick={handleOpenGalleryVideo}
        />
      )}

      {!atStart && (
        <span className={cx(`${BASE_CLASS}-arrow`, `${BASE_CLASS}-arrow--prev`)}>
          <GalleryIconButton icon={IconChevronLeft} onClick={scrollPrev} aria-label={i18n.t('COMMON.PREVIOUS')} />
        </span>
      )}
      {!atEnd && (
        <span className={cx(`${BASE_CLASS}-arrow`, `${BASE_CLASS}-arrow--next`)}>
          <GalleryIconButton icon={IconChevronRight} onClick={scrollNext} aria-label={i18n.t('COMMON.NEXT')} />
        </span>
      )}
    </>
  )
}

DesktopGallery.displayName = 'DesktopGallery'

DesktopGallery.propTypes = {
  mediaItems: PropTypes.arrayOf(mediaItemShape).isRequired,
  alt: PropTypes.string,
  galleryLabel: PropTypes.string.isRequired,
  hasVideo: PropTypes.bool,
  showControls: PropTypes.bool,
  videoIndex: PropTypes.number,
  canPlayInline: PropTypes.bool,
  isModalOpen: PropTypes.bool,
  onOpenGallery: PropTypes.func,
  onVideoButtonClick: PropTypes.func,
  onVideoPlayingChange: PropTypes.func
}

export default DesktopGallery
